import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { Container } from '@/components/ui/container'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import type { OpsVettingURLParams } from '@/features/ops-vetting/types'
import { ServiceProPhotosGrid } from '@/features/service-vetting/components/service-pro-photos-grid'
import { ServiceProPhotosHeader } from '@/features/service-vetting/components/service-pro-photos-header'
import { ServiceVettingCriteriaGalleryUploadButtonProPhoto } from '@/features/service-vetting/components/service-vetting-criteria-gallery-upload-button-pro-photo'
import { ServicesProPhotosOfferings } from '@/features/service-vetting/components/services-pro-photos-offerings'
import { useGetServiceProPhotos } from '@/features/service-vetting/query/use-get-service-pro-photo'
import { ProPhotosGrid } from '@/features/submission-pro-photos-upload/components/pro-photos-grid'
import { ProPhotosSkeleton } from '@/features/submission-pro-photos-upload/components/pro-photos-skeleton'
import { ProPhotosTitle } from '@/features/submission-pro-photos-upload/components/pro-photos-title'
import { useTranslation } from '@/translations/i18n'

export const ServiceProPhotos = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: OpsVettingURLParams }>()
	const {
		remappedData: service,
		isPending,
		isError
	} = useGetServiceProPhotos(urlParams.id)

	if (isPending || !service) {
		return (
			<ProPhotosSkeleton
				description={t('vetting:reordering.service.description')}
				serviceTitle={t('vetting:reordering.offerings')}
				title={t('vetting:reordering.service.title')}
			/>
		)
	}

	if (isError) {
		return (
			<Container fullWidth>
				<ErrorBox
					icon={<AlertTriangleIcon />}
					title={t('errors:default')}
				/>
			</Container>
		)
	}

	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			<ServiceProPhotosHeader service={service} />

			<ProPhotosTitle
				imagesLength={service.gallery.images.length}
				serviceTitle={t('vetting:reordering.offerings')}
				uploadButton={
					<ServiceVettingCriteriaGalleryUploadButtonProPhoto
						galleryId={service.gallery.id}
						serviceId={service.id}
					/>
				}
			/>

			<Separator className='bg-neutral-300' orientation='horizontal' />

			<ProPhotosGrid
				leftSlot={<ServiceProPhotosGrid service={service} />}
				rightSlot={<ServicesProPhotosOfferings service={service} />}
			/>
		</ScrollArea>
	)
}
ServiceProPhotos.displayName = 'ServiceProPhotos'
